import { useCallback, useContext, useEffect, useMemo } from "react";
import { createContext } from "react";
import {
  bindScrollFrameHandler,
  initScrollFramePosition,
  unbindScrollFrameHandler,
} from "./scroll-ui-frame";

type ScrollHandler = (scroll: number, delta: number) => void;

interface IScrollContext {
  bindScrollHandler: (handler: ScrollHandler) => void;
  unbindScrollHandler: (handler: ScrollHandler) => void;
}

const ScrollContext = createContext<IScrollContext | null>(null);

export function useScrollContext(): IScrollContext {
  const ctx = useContext(ScrollContext);
  if (ctx === null) {
    throw new Error("useScrollContext must be used within ScrollContextProvider");
  }
  return ctx;
}

interface IScrollContextProviderProps {
  children: React.ReactNode;
  enableNativeScrollListener?: boolean;
}

export function ScrollContextProvider(props: IScrollContextProviderProps) {
  const { children, enableNativeScrollListener = true } = props;

  const bindScrollHandler = useCallback((sc: ScrollHandler) => {
    bindScrollFrameHandler(sc);
  }, []);

  const unbindScrollHandler = useCallback((sc: ScrollHandler) => {
    unbindScrollFrameHandler(sc);
  }, []);

  useEffect(() => {
    if (!enableNativeScrollListener) {
      return;
    }

    initScrollFramePosition(window.scrollY);
  }, [enableNativeScrollListener]);

  const value = useMemo(
    () => ({
      bindScrollHandler,
      unbindScrollHandler,
    }),
    [bindScrollHandler, unbindScrollHandler],
  );

  return (
    <ScrollContext.Provider value={value}>
      {children}
    </ScrollContext.Provider>
  );
}
